import { ActionPerformed, PushNotifications, PushNotificationSchema } from "@capacitor/push-notifications";
import { useIonToast } from "@ionic/react";
import { useEffect, useMemo, useRef } from "react";
import { useHistory } from "react-router";

import useMe from "@hooks/useMe";
import useMutationWithValidation from "@hooks/useMutationWithValidation";
import {
  PushNotificationPlatformEnum,
  PushNotificationTokenCreateDocument,
  PushNotificationTokenCreateMutation,
  PushNotificationTokenCreateMutationVariables
} from "@typing/Generated";
import { IconList } from "@utils/iconUtils";
import { isIosNativeApp, isNativeApp } from "@utils/platformUtils";

const PushNotificationSetup = () => {
  const me = useMe();
  const registered = useRef<boolean>(false);
  const history = useHistory();

  const [present, dismiss] = useIonToast();

  const [createPushNotificationToken] = useMutationWithValidation<
    PushNotificationTokenCreateMutation,
    PushNotificationTokenCreateMutationVariables
  >(PushNotificationTokenCreateDocument, "pushNotificationToken");

  const platform = useMemo(() => {
    if (isIosNativeApp()) {
      return PushNotificationPlatformEnum.IOS;
    }
    return PushNotificationPlatformEnum.ANDROID;
  }, []);

  useEffect(() => {
    if (isNativeApp() && me) {
      if (!registered.current) {
        registered.current = true;

        PushNotifications.addListener("registration", registrationData => {
          // eslint-disable-next-line no-console
          console.log("++++++ Received a push notification registration " + JSON.stringify(registrationData));
          createPushNotificationToken({
            platform,
            token: registrationData.value
          });
        });

        const registerForNotifications = async () => {
          let permStatus = await PushNotifications.checkPermissions();

          if (permStatus.receive === "prompt") {
            permStatus = await PushNotifications.requestPermissions();
          }

          if (permStatus.receive === "granted") {
            await PushNotifications.register();
          }
        };

        registerForNotifications();

        PushNotifications.addListener("registrationError", error => {
          // eslint-disable-next-line no-console
          console.log("+++++ Error on push notification registration: " + JSON.stringify(error));
        });

        const openNotificationUrl = (url: string) => {
          const properUrl = new URL(url);
          const pathWithQuery = properUrl.pathname + properUrl.search;
          if (pathWithQuery) {
            history.push(pathWithQuery);
          }
        };

        PushNotifications.addListener("pushNotificationReceived", (notification: PushNotificationSchema) => {
          // The app is open and we got a notification. The system one will not be displayed, so we need to make our own.
          // Show a toast!
          // eslint-disable-next-line no-console
          console.log("++++++ Received a push notification received " + JSON.stringify(notification));
          present({
            buttons: [
              {
                handler: () => {
                  openNotificationUrl(notification.data.url);
                },
                icon: IconList.arrowRightFromBracket
              },
              { handler: () => dismiss(), icon: IconList.xmark }
            ],
            cssClass: "pushNotificationToast",
            duration: 30_000,
            message: notification.body || notification.data.body,
            position: "top"
          });
        });

        PushNotifications.addListener("pushNotificationActionPerformed", (notification: ActionPerformed) => {
          // eslint-disable-next-line no-console
          console.log("++++++ Received a push notification action performed " + JSON.stringify(notification));
          // Method called when tapping on a notification when the app is not open. Push notifications can contain
          // structured data about actions that you want them to take, and ideally we would implement that, but for
          // now we're just opening up the URL that we assume the notification to contain.
          openNotificationUrl(notification.notification.data.url);
        });
      }

      return () => {
        PushNotifications.removeAllListeners();
      };
    }
  }, [createPushNotificationToken, dismiss, history, me, platform, present]);

  return null;
};

export default PushNotificationSetup;
